// problemTypeFor is S1's (src/problem.ts, `urn:fxmate:reason:<CODE>`): ProblemSchema refines
// `type` against it, so a second definition here would produce bodies the schema rejects.
import { ProblemSchema, problemTypeFor, type Problem } from './problem';
import type { ReasonCode } from './reason-code';

/** English title for logs only (master X2); the user sees `t('reason.<code>')`. */
export function defaultProblemTitle(reasonCode: ReasonCode): string {
  const words = reasonCode.toLowerCase().replaceAll('_', ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** Input of {@link makeProblem}. */
export interface MakeProblemInput {
  readonly status: number;
  readonly reasonCode: ReasonCode;
  /** Values the reason text interpolates, e.g. `{ minutes: 10 }` (C2 §5.4, S4 R13). */
  readonly params?: Problem['params'];
}

/** Builds the Problem body for a reason code; used by fixtures, MSW and the API error handler. */
export function makeProblem(input: MakeProblemInput): Problem {
  return {
    type: problemTypeFor(input.reasonCode),
    title: defaultProblemTitle(input.reasonCode),
    status: input.status,
    reasonCode: input.reasonCode,
    ...(input.params === undefined ? {} : { params: input.params }),
  };
}

/**
 * Reads any error body as a Problem, for the Mini App API client.
 *
 * An unknown `reasonCode` (a server newer than the client, after C2 adds codes) or a body that is
 * not a Problem becomes `INTERNAL_ERROR` with the HTTP status kept. Nothing from the raw body is
 * copied, so no broker or Telegram text can reach the screen (I9, I12, C1 spec R16).
 */
export function parseProblem(body: unknown, httpStatus: number): Problem {
  const parsed = ProblemSchema.safeParse(body);
  if (parsed.success) {
    return parsed.data;
  }
  return {
    type: problemTypeFor('INTERNAL_ERROR'),
    title: 'Unrecognized error response',
    status: httpStatus,
    reasonCode: 'INTERNAL_ERROR',
  };
}
